import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PARTS_REFRESH_QUEUE } from "../constants/urls";

const STORAGE_KEY = "bommanager.partRefreshQueue";
const POLL_MS = 2000;
const PartRefreshQueueContext = createContext(null);

const mapJob = (job) => ({
  id: String(job.id),
  partId: Number(job.part_id),
  partNumber: String(job.part_number || ""),
  manufacturer: String(job.manufacturer || ""),
  status: String(job.status || "pending"),
  progress: Number(job.progress || 0),
  message: String(job.message || ""),
  catalogRow: job.catalog_row || null,
});

const mergeJobs = (current, incoming) => {
  const byId = new Map(current.map((item) => [String(item.id), item]));
  incoming.forEach((item) => {
    byId.set(String(item.id), item);
  });
  return Array.from(byId.values()).sort((a, b) => Number(a.id) - Number(b.id));
};

const readStoredQueue = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const PartRefreshQueueProvider = ({ token, children }) => {
  const [items, setItems] = useState(() => readStoredQueue());
  const [panelOpen, setPanelOpen] = useState(false);
  const [lastCompleted, setLastCompleted] = useState(null);
  const itemsRef = useRef(items);
  const tokenRef = useRef(token);
  const seenSuccessRef = useRef(new Set());
  tokenRef.current = token;

  useEffect(() => {
    itemsRef.current = items;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-80)));
    } catch (error) {
      // Ignore quota / private-mode failures.
    }
  }, [items]);

  const applyJobs = useCallback((jobs, completed = []) => {
    const mapped = (jobs || []).map(mapJob);
    const completedMapped = (completed || []).map(mapJob);
    const activeIds = new Set(mapped.map((item) => String(item.id)));
    const newlyDone = [];

    const markDone = (item) => {
      if (seenSuccessRef.current.has(String(item.id))) {
        return;
      }
      seenSuccessRef.current.add(String(item.id));
      newlyDone.push(item);
    };

    completedMapped.forEach(markDone);

    const vanishedAsSuccess = itemsRef.current
      .filter(
        (item) =>
          (item.status === "pending" || item.status === "syncing") &&
          !String(item.id).startsWith("local-") &&
          !activeIds.has(String(item.id)),
      )
      .map((item) => {
        const done = completedMapped.find(
          (row) => String(row.id) === String(item.id) || row.partId === item.partId,
        );
        const nextItem = {
          ...item,
          ...(done || {}),
          status: "success",
          progress: 100,
          message: "",
        };
        markDone(nextItem);
        return nextItem;
      });

    if (newlyDone.length) {
      setLastCompleted({
        rows: newlyDone,
        at: Date.now(),
      });
    }

    const next = mergeJobs(
      itemsRef.current.filter(
        (item) => !vanishedAsSuccess.some((row) => String(row.id) === String(item.id)),
      ),
      mapped.concat(completedMapped).concat(vanishedAsSuccess),
    );
    itemsRef.current = next;
    setItems(next);
    return next;
  }, []);

  const requestQueue = useCallback(async (method, body) => {
    const authToken = tokenRef.current;
    if (!authToken) {
      return { jobs: [], completed: [] };
    }
    const res = await fetch(PARTS_REFRESH_QUEUE, {
      method,
      headers: {
        authorization: "Bearer " + authToken,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const json = await res.json();
    if (!json || !(json.code >= 200 && json.code < 400)) {
      throw new Error((json && json.message) || "Failed to update DigiKey refresh queue.");
    }
    return {
      jobs: Array.isArray(json.data && json.data.jobs) ? json.data.jobs : [],
      completed: Array.isArray(json.data && json.data.completed)
        ? json.data.completed
        : [],
    };
  }, []);

  const pollQueue = useCallback(async () => {
    try {
      const { jobs, completed } = await requestQueue("GET");
      applyJobs(jobs, completed);
    } catch (error) {
      // Keep showing the last known queue if a poll fails.
    }
  }, [applyJobs, requestQueue]);

  const isRunning = items.some(
    (item) => item.status === "pending" || item.status === "syncing",
  );

  useEffect(() => {
    if (!token || !isRunning) {
      return undefined;
    }
    const timer = setInterval(pollQueue, POLL_MS);
    pollQueue();
    return () => clearInterval(timer);
  }, [token, isRunning, pollQueue]);

  const enqueue = useCallback(
    async (parts) => {
      const incoming = (Array.isArray(parts) ? parts : [parts]).filter(
        (part) => part && part.id != null,
      );
      if (!incoming.length) {
        return;
      }
      setPanelOpen(true);
      const optimistic = incoming.map((part) => ({
        id: `local-${part.id}`,
        partId: part.id,
        partNumber: part.part_number || "",
        manufacturer: part.manufacturer || "",
        status: "pending",
        progress: 0,
        message: "",
        catalogRow: null,
      }));
      const next = mergeJobs(itemsRef.current, optimistic);
      itemsRef.current = next;
      setItems(next);
      try {
        const { jobs, completed } = await requestQueue("POST", {
          parts: incoming.map((part) => ({
            id: part.id,
            part_number: part.part_number,
            manufacturer: part.manufacturer,
          })),
        });
        const withoutLocal = itemsRef.current.filter(
          (item) => !String(item.id).startsWith("local-"),
        );
        itemsRef.current = withoutLocal;
        applyJobs(jobs, completed);
      } catch (error) {
        const failed = itemsRef.current.map((item) =>
          String(item.id).startsWith("local-")
            ? { ...item, status: "failed", progress: 100, message: error.message }
            : item,
        );
        itemsRef.current = failed;
        setItems(failed);
      }
    },
    [applyJobs, requestQueue],
  );

  const retryFailed = useCallback(async () => {
    setPanelOpen(true);
    try {
      const { jobs, completed } = await requestQueue("POST", { retry_failed: true });
      applyJobs(jobs, completed);
    } catch (error) {
      // Dock already shows failed rows.
    }
  }, [applyJobs, requestQueue]);

  const clearFinished = useCallback(() => {
    const next = itemsRef.current.filter(
      (item) => item.status === "pending" || item.status === "syncing",
    );
    itemsRef.current = next;
    setItems(next);
  }, []);

  const value = useMemo(
    () => ({
      items,
      panelOpen,
      setPanelOpen,
      enqueue,
      retryFailed,
      clearFinished,
      lastCompleted,
      isRunning,
    }),
    [items, panelOpen, enqueue, retryFailed, clearFinished, lastCompleted, isRunning],
  );

  return (
    <PartRefreshQueueContext.Provider value={value}>
      {children}
    </PartRefreshQueueContext.Provider>
  );
};

export const usePartRefreshQueue = () => {
  const value = useContext(PartRefreshQueueContext);
  if (!value) {
    return {
      items: [],
      panelOpen: false,
      setPanelOpen: () => {},
      enqueue: () => {},
      retryFailed: () => {},
      clearFinished: () => {},
      lastCompleted: null,
      isRunning: false,
    };
  }
  return value;
};
